<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/useAuthStore';
import dartsHubLogo from '@/assets/dartshub-logo-blanc.png';

const authStore = useAuthStore();
const router = useRouter();
const mobileOpen = ref(false);

const logout = () => {
  authStore.logout();
  router.push('/login');
};

const navLinks = [
  { label: 'Utilisateurs', to: '/admin/users',    icon: 'fa-users',           adminOnly: true },
  { label: 'Rôles',        to: '/admin/roles',     icon: 'fa-shield-halved',   adminOnly: true },
  { label: 'Clubs',        to: '/admin/clubs',     icon: 'fa-building',        adminOnly: true },
  { label: 'Comités',      to: '/admin/comites',   icon: 'fa-sitemap',         adminOnly: true },
  { label: 'Ligues',       to: '/admin/ligues',    icon: 'fa-flag',            adminOnly: true },
  { label: 'Historique',   to: '/admin/org-history', icon: 'fa-clock-rotate-left', adminOnly: true },
  { label: 'Histoire',     to: '/admin/history',  icon: 'fa-landmark',        adminOnly: true },
  { label: 'Licences',     to: '/admin/licenses',  icon: 'fa-id-card',         adminOnly: true },
  { label: 'Affiliations', to: '/admin/affiliations', icon: 'fa-file-signature', adminOnly: true },
  { label: 'Accès',        to: '/admin/access-requests', icon: 'fa-key',        adminOnly: true },
  { label: 'Premium', to: '/admin/events-premium', icon: 'fa-crown', adminOnly: true },
  { label: 'Partenaires',  to: '/admin/partners',  icon: 'fa-handshake',       adminOnly: true },
  { label: 'Monitoring',   to: '/admin/monitor',   icon: 'fa-gauge-high',      adminOnly: true },
  { label: 'Nettoyage',    to: '/admin/cleanup',   icon: 'fa-broom',           adminOnly: true },
];

const visibleLinks = navLinks.filter(l => !l.adminOnly || authStore.isSuperAdmin);
</script>

<template>
  <nav class="navbar">
    <div class="navbar-inner">
      <!-- Logo -->
      <RouterLink to="/" class="navbar-brand">
        <img :src="dartsHubLogo" alt="DartsHub" class="brand-logo" />
      </RouterLink>

      <!-- Desktop links -->
      <div class="navbar-links">
        <RouterLink v-for="link in visibleLinks" :to="link.to" class="nav-link" activeClass="nav-link-active"
          :key="link.to">
          <i :class="`fa-solid ${link.icon}`" />
          {{ link.label }}
        </RouterLink>
      </div>

      <!-- Right: profile + logout -->
      <div class="navbar-right">
        <RouterLink to="/profile" class="profile-btn">
          <img v-if="authStore.profilePictureUrl" :src="authStore.profilePictureUrl" class="nav-avatar" alt="avatar" />
          <div v-else class="nav-initials">
            {{ (authStore.firstname?.[0] || '') + (authStore.name?.[0] || '') }}
          </div>
          <span class="nav-name">{{ authStore.firstname }}</span>
        </RouterLink>

        <button class="btn btn-ghost btn-sm" title="Se déconnecter" @click="logout">
          <i class="fa-solid fa-right-from-bracket" />
        </button>

        <!-- Hamburger -->
        <button class="hamburger" @click="mobileOpen = !mobileOpen">
          <i :class="mobileOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'" />
        </button>
      </div>
    </div>

    <!-- Mobile menu -->
    <div v-if="mobileOpen" class="mobile-menu">
      <RouterLink v-for="link in visibleLinks" :to="link.to" class="mobile-link" activeClass="mobile-link-active"
        @click="mobileOpen = false" :key="link.to">
        <i :class="`fa-solid ${link.icon}`" />
        {{ link.label }}
      </RouterLink>
      <RouterLink to="/profile" class="mobile-link" @click="mobileOpen = false">
        <i class="fa-solid fa-user" /> Mon profil
      </RouterLink>
      <button class="mobile-link mobile-logout" @click="logout">
        <i class="fa-solid fa-right-from-bracket" /> Se déconnecter
      </button>
    </div>
  </nav>
</template>

<style scoped>
.navbar {
  background: linear-gradient(135deg, var(--dh-header-from) 0%, var(--primary) 60%, var(--dh-header-to) 100%);
  box-shadow: 0 2px 12px color-mix(in srgb, var(--primary) 20%, transparent);
  position: sticky;
  top: 0;
  z-index: 50;
}

.navbar-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 1.5rem;
  min-height: 58px;
  display: flex;
  align-items: center;
  gap: 0.5rem 2rem;
}

/* ── Brand ── */
.navbar-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  flex-shrink: 0;
}

.brand-logo {
  height: 30px;
  width: auto;
  flex-shrink: 0;
}

/* ── Links ── */
.navbar-links {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  flex: 1 1 auto;
  /* min-width: 0 laisse ce conteneur rétrécir sous sa largeur de contenu (comportement
     par défaut d'un enfant flex sans ça), et flex-wrap le fait passer à la ligne plutôt
     que de pousser .navbar-right (profil/déconnexion) hors de l'écran -- avec jusqu'à 12
     liens admin, un défilement horizontal caché (ancienne version) laissait les derniers
     liens inatteignables sans indice visuel qu'il fallait scroller. */
  min-width: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.15s, background 0.15s;
}

.nav-link:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
}

.nav-link-active {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

/* ── Right side ── */
.navbar-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
  flex-shrink: 0;
}

.profile-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  transition: background 0.15s;
}

.profile-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

.nav-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.nav-initials {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #ffffff;
}

.nav-name {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Override btn-ghost for navbar context */
.navbar-right :deep(.btn-ghost) {
  color: rgba(255, 255, 255, 0.75);
  border-color: rgba(255, 255, 255, 0.25);
}

.navbar-right :deep(.btn-ghost:hover) {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.4);
}

.hamburger {
  display: none;
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0.25rem;
}

/* ── Mobile menu ── */
.mobile-menu {
  display: none;
  flex-direction: column;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding: 0.5rem;
  background: linear-gradient(135deg, var(--dh-header-from) 0%, var(--primary) 100%);
}

.mobile-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: background 0.15s, color 0.15s;
}

.mobile-link:hover,
.mobile-link-active {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
}

.mobile-logout {
  color: #fca5a5;
}

@media (max-width: 768px) {
  .navbar-links { display: none; }
  .nav-name { display: none; }
  .hamburger { display: block; }
  .mobile-menu { display: flex; }
}
</style>
